import React, { useEffect, useState, useCallback } from "react";

const PrinterSetup = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  // Load BrowserPrint SDK
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "http://nzen/R50B3/salescrm/js/BrowserPrint-3.0.216.min.js";
    script.async = true;
    script.onload = () => {
      console.log("BrowserPrint SDK loaded");
      setSdkLoaded(true);
    };
    script.onerror = () => {
      setError("Failed to load BrowserPrint SDK");
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Setup printers once SDK is loaded
  useEffect(() => {
    if (!sdkLoaded || !window.BrowserPrint) {
      return;
    }

    const setupPrinters = () => {
      const BrowserPrint = window.BrowserPrint;
      
      // Get default device first
      BrowserPrint.getDefaultDevice(
        "printer",
        (defaultDevice) => {
          console.log("Default device found:", defaultDevice.name);
          setSelectedDevice(defaultDevice);
          setDevices([defaultDevice]);

          // Then discover other devices
          BrowserPrint.getLocalDevices(
            (deviceList) => {
              console.log("Device list:", deviceList);
              const uniqueDevices = [defaultDevice];
              
              deviceList.forEach((device) => {
                if (device.uid !== defaultDevice.uid) {
                  uniqueDevices.push(device);
                }
              });
              
              setDevices(uniqueDevices);
              setLoading(false);
            },
            (err) => {
              console.error("Error getting local devices:", err);
              // Still set loading to false as we have default device
              setLoading(false);
            },
            "printer"
          );
        },
        (err) => {
          console.error("Error getting default device:", err);
          setError("No printer found. Please ensure BrowserPrint is running.");
          setLoading(false);
        }
      );
    };

    setupPrinters();
  }, [sdkLoaded]);

  const handleDeviceChange = (e) => {
    const deviceUid = e.target.value;
    const device = devices.find((d) => d.uid === deviceUid);
    if (device) {
      setSelectedDevice(device);
      console.log("Selected device:", device.name);
    }
  };

  const writeToSelectedPrinter = useCallback((data) => {
    if (!selectedDevice) {
      alert("No printer selected");
      return;
    }

    selectedDevice.send(
      data,
      () => {
        console.log("Data sent successfully");
        alert("Print job sent successfully");
      },
      (err) => {
        console.error("Print error:", err);
        alert("Print error: " + err);
      }
    );
  }, [selectedDevice]);

  const readFromSelectedPrinter = useCallback(() => {
    if (!selectedDevice) {
      alert("No printer selected");
      return;
    }

    selectedDevice.read(
      (data) => {
        if (data === undefined || data === null || data === "") {
          alert("No response from device");
        } else {
          console.log("Printer response:", data);
          alert("Printer response: " + data);
        }
      },
      (err) => {
        console.error("Read error:", err);
        alert("Read error: " + err);
      }
    );
  }, [selectedDevice]);

  const sendFile = useCallback((fileUrl) => {
    if (!selectedDevice) {
      alert("No printer selected");
      return;
    }

    const baseUrl = window.location.href.substring(
      0,
      window.location.href.lastIndexOf("/")
    );
    const fullUrl = baseUrl + "/" + fileUrl;

    selectedDevice.sendFile(
      fullUrl,
      () => {
        console.log("File sent successfully");
        alert("File sent successfully");
      },
      (err) => {
        console.error("Send file error:", err);
        alert("Send file error: " + err);
      }
    );
  }, [selectedDevice]);

  const sendImage = useCallback((imageUrl) => {
    if (!selectedDevice) {
      alert("No printer selected");
      return;
    }

    const baseUrl = window.location.href.substring(
      0,
      window.location.href.lastIndexOf("/")
    );
    const fullUrl = baseUrl + "/" + imageUrl;

    selectedDevice.convertAndSendFile(
      fullUrl,
      () => {
        console.log("Image sent successfully");
        alert("Image sent successfully");
      },
      (err) => {
        console.error("Send image error:", err);
        alert("Send image error: " + err);
      }
    );
  }, [selectedDevice]);

  // Sample ZPL command for testing
  const testPrint = () => {
    const zplCommand = `^XA
^FO50,50^A0N,50,50^FDTest Print^FS
^FO50,120^A0N,30,30^FDZebra Printer Test^FS
^XZ`;
    writeToSelectedPrinter(zplCommand);
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", fontFamily: "Arial" }}>
        <p>Loading printers... Please ensure BrowserPrint is running.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", fontFamily: "Arial" }}>
        <p style={{ color: "red" }}>{error}</p>
        <p style={{ fontSize: "14px", marginTop: "10px" }}>
          Make sure the Zebra BrowserPrint application is installed and running.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      <h2>Zebra Printer Control</h2>
      
      <div style={{ marginBottom: "20px" }}>
        <label style={{ fontWeight: "bold", marginRight: "10px" }}>
          Select Printer:
        </label>
        <select
          value={selectedDevice?.uid || ""}
          onChange={handleDeviceChange}
          style={{ padding: "5px", fontSize: "14px" }}
        >
          {devices.map((device) => (
            <option key={device.uid} value={device.uid}>
              {device.name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginTop: "20px" }}>
        <h3>Actions:</h3>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            onClick={testPrint}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Print Test Label
          </button>
          
          <button
            onClick={readFromSelectedPrinter}
            style={{
              padding: "10px 20px",
              backgroundColor: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Read from Printer
          </button>
        </div>
      </div>

      <div style={{ marginTop: "20px", fontSize: "12px", color: "#666" }}>
        <p><strong>Selected Printer:</strong> {selectedDevice?.name || "None"}</p>
        <p><strong>Total Printers Found:</strong> {devices.length}</p>
      </div>
    </div>
  );
};

export default PrinterSetup;